import type { DeepPartial } from "utility-types"
import { type EventMap, Events, type Messages } from "./liqa.d"
import { ShadowRealm } from "./utils/shadow-realm"

export { preload, type PreloadOptions } from "./preload"

export type {
  CaptureFormat,
  ImageCapture,
  CaptureTransformations,
  ErrorCodes,
  LiqaError,
  AnalyticsEventsMap,
  AnalyticsDetails,
} from "./liqa.d"

/**
 * The library version.
 * @example
 * ```js
 * "6.0.0"
 * ```
 * @category Misc
 */
export const VERSION = import.meta.env.VERSION

/**
 * The Web component name.
 * @category Misc
 */
export const NAME = "hautai-liqa" as const

// TODO: document all the black magic going here and particularly why it‘s implemented as it‘s implemented

/**
 * LIQA Web component available as `<hautai-liqa></hautai-liqa>` tag.
 * @example
 * ```html
 * <hautai-liqa license="xxx-xxx-xxx"></hautai-liqa>
 * ```
 * @category Component
 */
export class LiqaElement extends HTMLElement {
  private readonly __root__: ShadowRoot
  private __widget__: Promise<{
    component: ReturnType<typeof import("./liqa").create>
    globalErrorTracker: { destroy: () => void }
  }> | null

  /** @hidden */
  constructor() {
    super()

    const mode = typeof __LIQA_E2E__ !== "undefined" && __LIQA_E2E__ ? "open" : "closed"

    this.__root__ = this.attachShadow({ mode })
    this.__widget__ = null
  }

  protected connectedCallback() {
    // https://developer.mozilla.org/en-US/docs/Web/API/Web_components/Using_custom_elements#using_the_lifecycle_callbacks
    if (!this.isConnected) return

    if (this.__widget__) return

    const root = this.__root__

    // Isolated JavaScript execution context
    const realm = new ShadowRealm(root)

    if (typeof __LIQA_E2E__ !== "undefined" && __LIQA_E2E__) {
      realm["_getGlobal"]().then((realm) => {
        // use global getUserMedia instead of the isolated one from the Realm to allow test environment to mock it
        Object.getPrototypeOf(realm.navigator.mediaDevices).getUserMedia = Object.getPrototypeOf(
          self.navigator.mediaDevices,
        ).getUserMedia
      })
    }

    this.__widget__ = Promise.resolve()
      .then(() =>
        realm
          // enable global error tracking
          .importValue<typeof import("./telemetry/errors").setup>(
            resolve("./telemetry/errors/index"),
            "setup",
          )
          .then((setup) => setup())
          .then((destroy) => ({ destroy })),
      )
      .then((globalErrorTracker) =>
        realm
          .importValue<typeof import("./liqa").create>(resolve("./liqa"), "create")
          .then((create) => {
            const attrs = attrs2props<LiqaAttributes>(this.attributes)
            const config: LiqaConfig = {
              ...attrs,
              messages: attrs.messages ? JSON.parse(attrs.messages) : undefined,
              sources: attrs.sources
                ? (attrs.sources?.split(",").map((s) => s.trim()) as LiqaConfig["sources"])
                : undefined,
            }

            const component = create(root, config)

            return { component, globalErrorTracker }
          }),
      )
      .catch((error) => {
        // track any errors during the component loading and initialization
        const license = this.attributes.getNamedItem("license")?.value

        realm
          .importValue(resolve("./telemetry/errors/index"), "log")
          .then((log) => log(error, { tags: { license } }))

        throw error
      })
  }

  protected disconnectedCallback() {
    if (!this.__widget__) return

    const root = this.__root__
    const children = new Set(root.children)

    this.__widget__.then(({ component, globalErrorTracker }) => {
      globalErrorTracker.destroy()
      component?.destroy()

      for (const child of root.children) if (children.has(child)) root.removeChild(child)
    })
    this.__widget__ = null
  }

  /** @internal */
  override addEventListener(...args: unknown[]): void {
    if (args[0] === Events.LOADED) {
      args[0] = Events.READY
      console.warn(
        `[LIQA] Deprecation warning: the "${Events.LOADED}" event has been deprecated. Subscribe to "${Events.READY}" event instead.`,
      )
    }

    super.addEventListener(...(args as [string, EventListenerOrEventListenerObject]))
  }
}

export interface LiqaElement {
  /** Subscribes the event listener to the given LIQA {@link EventMap event}. */
  addEventListener<K extends keyof EventMap>(
    type: K,
    listener: (this: LiqaElement, ev: CustomEvent<EventMap[K]>) => any,
    options?: boolean | AddEventListenerOptions,
  ): void
  /** @hidden */
  addEventListener(
    type: string,
    listener: EventListenerOrEventListenerObject,
    options?: boolean | AddEventListenerOptions,
  ): void
  /** Unsubscribes the event listener from the given LIQA {@link EventMap event}. */
  removeEventListener<K extends keyof EventMap>(
    type: K,
    listener: (this: LiqaElement, ev: CustomEvent<EventMap[K]>) => any,
    options?: boolean | EventListenerOptions,
  ): void
  /** @hidden */
  removeEventListener(
    type: string,
    listener: EventListenerOrEventListenerObject,
    options?: boolean | EventListenerOptions,
  ): void
}

/**
 * LIQA Web component HTML attributes to configure the component look and behavior.
 * @example
 * ```html
 * <hautai-liqa
 *   license="xxx-xxx-xxx"
 *   styles="@import url('/path/to/custom-liqa-styles.css')"
 * >
 * </hautai-liqa>
 * ```
 * @category Component
 */
export type LiqaAttributes = {
  /** The license key obtained from {@link https://haut.ai/#contacts Haut.ai }. */
  license: string
  /**
   * A string containing valid CSS rules.
   * @example
   * "@import url('/path/to/custom-liqa-styles.css')"
   */
  styles?: string
  /**
   * A string containing a valid JSON object whose keys are messages keys and values are messages texts.
   * @example
   * "{ \"preview\": { \"submit\": \"Confirm\" } }"
   */
  messages?: string
  /**
   * A preset used to customize AI quality criteria and user flow.
   * @default "face"
   */
  preset?: "face" | "hair"
  /**
   * A list of sources to capture the image from.
   * If more than one source is specified, the user will be able to switch between sources.
   * If only one "camera" source is specified, the camera device will be used to capture the image.
   * If only one "upload" source is specified, the file upload will be used to capture the image.
   * @default "camera"
   */
  sources?: "camera" | "upload" | "camera,upload"
  /**
   * Image capture mode for `"camera"` source.
   * `"auto"` means LIQA will automatically take a photo when all AI quality criteria met.
   * `"manual"` means LIQA will allow the user to take a photo on their own via a button.
   * Default value depends on the `preset` option: `"auto"` for `"face"`, `"manual"` for `"hair"`.
   */
  capture?: "auto" | "manual"
}

/**
 * The component event names and corresponding event details.
 * @example
 * ```html
 * <hautai-liqa
 *   license="xxx-xxx-xxx"
 *   onready="console.log('LIQA is ready!')"
 *   onerror="console.warn('An error occurred:', event.detail)"
 * >
 * </hautai-liqa>
 * ```
 * @category Component
 */
export type { EventMap as LiqaEventMap }

/** Register web component and make it available as an HTML tag */
if (!customElements.get(NAME)) customElements.define(NAME, LiqaElement)

/*
 * Utils
 */

/** "foo-bar-baz" -> "fooBarBaz" */
const camelCase = (str: string) => str.replace(/-(\w)/g, (_, c) => c.toUpperCase())

/** cast HTMLElement.attributes to Svelte component props */
const attrs2props = <R extends Record<string, unknown>>(attrs: Element["attributes"]) =>
  Object.fromEntries(Array.from(attrs, ({ name, value }) => [camelCase(name), value])) as R

/**
 * Ponyfill for [import.meta.resolve](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/import.meta/resolve)
 * until it has a better browser support
 */
function resolve(moduleName: string) {
  // the ["url"] syntax is used to trick Vite to note transpile the `import.meta.url` into the `self.location` during a build
  return new URL(moduleName, import.meta["url"])
}

/*
 * LIQA Services v4 Backward compatibility
 */

/**
 * An imperative way to create LIQA web component.
 * @example
 * ```javascript
 * const liqa = new Liqa({
 *   license: "xxx-xxx-xxx",
 *   target: document.querySelector("#liqa-container"),
 *   styles: "@import url('/path/to/custom-liqa-styles.css')"
 * })
 * ```
 * @category Component (imperative)
 * @deprecated Use the {@link LiqaElement <hautai-liqa>} web component instead.
 */
export function Liqa(config: LiqaConfig): LiqaElement {
  const liqa = new LiqaElement()
  const { target, ...attributes } = config

  Object.entries(attributes).forEach(([key, value]) => {
    let str = String(value)
    if (str === "[object Object]") str = JSON.stringify(value)
    liqa.setAttribute(key, str)
  })

  if (target) {
    const container = typeof target === "string" ? document.querySelector(target) : target
    if (container) container.appendChild(liqa)
    else console.warn(`[LIQA] Target element "${target}" does not exists.`)
  }

  return liqa
}

/**
 * Options to configure the LIQA‘s look and behavior.
 * @category Component (imperative)
 * @deprecated Use the {@link LiqaElement <hautai-liqa>} web component with the {@link LiqaAttributes} instead.
 */
export type LiqaConfig = {
  /** The license key obtained from {@link https://haut.ai/#contacts Haut.ai }.  */
  license: string
  /**
   * Query selector to the target element or HTMLElement to render LIQA to.
   * @example
   * document.querySelector("#liqa-container")
   */
  target?: Element | string
  /**
   * A string containing valid CSS rules.
   * @example
   * "@import url('/path/to/custom-liqa-styles.css')"
   */
  styles?: string
  /**
   * An object whose keys are messages keys and values are messages texts.
   * @example
   * { "preview": { "submit": "Confirm" } }
   */
  messages?: DeepPartial<typeof Messages>
  /**
   * A preset used to customize AI quality criteria and user flow.
   * @default "face"
   */
  preset?: "face" | "hair"
  /**
   * A list of sources to capture the image from.
   * If more than one source is specified, the user will be able to switch between sources.
   * If only one "camera" source is specified, the camera device will be used to capture the image.
   * If only one "upload" source is specified, the file upload will be used to capture the image.
   * @default ["camera"]
   */
  sources?: ["camera"] | ["upload"] | ["camera", "upload"]
  /**
   * Image capture mode for `"camera"` source.
   * `"auto"` means LIQA will automatically take a photo when all AI quality criteria met.
   * `"manual"` means LIQA will allow the user to take a photo on their own via a button.
   * Default value depends on the `preset` option: `"auto"` for `"face"`, `"manual"` for `"hair"`.
   */
  capture?: "auto" | "manual"
}
